import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useMemo } from 'react'

import { SiteLegalAgreementsEnum } from '../graphql/gatsby'
import { legalAgreementIds } from '../utils/legalAgreements'
import { userCategories, userClaimIds } from '../utils/userClaims'
import useSiteMetadata from './useSiteMetadata'

interface UserProps {
  category: unknown
  hipaaCoveredEntity: unknown
  acceptedAgreements: unknown
}

interface AppliesToUser {
  (props: UserProps): { applies: boolean; message?: string }
}

// application scopes that can be guarded by legal agreements
export enum GuardedScope {
  CHECKOUT = 'checkout',
}

export interface LegalAgreement {
  id: string
  appliesToUser: AppliesToUser
  scopesToGuard: GuardedScope[]
}

export interface RequiredForScope {
  (scope: GuardedScope): string[]
}

export interface UseLegalAgreementsResult {
  accepted: Record<string, string>
  pending: string[]
  allowedScopes: GuardedScope[]
  requiredForScope: RequiredForScope
}

const and =
  (a: AppliesToUser, b: AppliesToUser): AppliesToUser =>
  (userProps: UserProps) => {
    const resultA = a(userProps)
    return resultA.applies ? b(userProps) : resultA
  }

const noLongerApplies: AppliesToUser = () => ({
  applies: false,
  message: 'No longer applicable',
})

const appliesToAllUsers: AppliesToUser = () => ({ applies: true })

/**
 * Applies to professional user unless they've accepted a certain agreement.
 * Ref: https://app.shortcut.com/designsforhealth/story/94668/implement-update-rxdfhend
 */
const appliesToProfessionalUser =
  (unlessHasAccepted?: string): AppliesToUser =>
  ({ acceptedAgreements, category }) =>
    (category === userCategories.practitioner || category === userCategories.otherProfessional) &&
    !(
      !!acceptedAgreements &&
      typeof acceptedAgreements === 'object' &&
      (unlessHasAccepted ?? '') in acceptedAgreements
    )
      ? { applies: true }
      : { applies: false, message: `Not applicable to user category ${category}` }

const appliesToCoveredEntity: AppliesToUser = ({ hipaaCoveredEntity }) =>
  hipaaCoveredEntity !== false
    ? { applies: true }
    : { applies: false, message: 'Not applicable to non-covered entity' }

const agreementsBySet: Record<SiteLegalAgreementsEnum, LegalAgreement[]> = {
  US: [
    {
      id: legalAgreementIds.rxDFHendUSJun2024,
      appliesToUser: appliesToProfessionalUser(legalAgreementIds.rxDFHendUSAug2021),
      // guard checkout if pending
      scopesToGuard: [GuardedScope.CHECKOUT],
    },
    {
      id: legalAgreementIds.rxDFHendUSAug2021,
      appliesToUser: noLongerApplies,
      // guard checkout if pending
      scopesToGuard: [GuardedScope.CHECKOUT],
    },
    {
      id: legalAgreementIds.rxDFHendUSMay2021,
      appliesToUser: noLongerApplies,
      scopesToGuard: [],
    },
    {
      id: legalAgreementIds.baa,
      appliesToUser: and(appliesToProfessionalUser(), appliesToCoveredEntity),
      // guard checkout if pending
      scopesToGuard: [GuardedScope.CHECKOUT],
    },
  ],
  PL: [
    {
      id: legalAgreementIds.plOct022023,
      appliesToUser: noLongerApplies,
      // guard checkout if pending
      scopesToGuard: [GuardedScope.CHECKOUT],
    },
    {
      id: legalAgreementIds.plOct052023,
      appliesToUser: appliesToAllUsers,
      // guard checkout if pending
      scopesToGuard: [GuardedScope.CHECKOUT],
    },
  ],
  CA: [
    {
      id: legalAgreementIds.rxDFHendCASept2021,
      appliesToUser: appliesToProfessionalUser(),
      // guard checkout if pending
      scopesToGuard: [GuardedScope.CHECKOUT],
    },
  ],
}

/**
 * Hook to determine which agreements are accepted, not required, or pending
 */
const useLegalAgreements = (): UseLegalAgreementsResult => {
  const { isAuthenticated, user } = useAuth0()
  const { legalAgreements } = useSiteMetadata() || {}

  const agreements = useMemo(
    () =>
      legalAgreements && legalAgreements in agreementsBySet ? agreementsBySet[legalAgreements] : [],
    [legalAgreements],
  )

  const summary = useMemo<Omit<UseLegalAgreementsResult, 'requiredForScope'>>(() => {
    if (!isAuthenticated || !user) {
      return {
        accepted: {},
        pending: [],
        allowedScopes: [GuardedScope.CHECKOUT],
      }
    }

    const userAcceptedAgreements = user[userClaimIds.acceptedAgreements] || {}
    const userCategory = user[userClaimIds.category]
    const userHIPAACoveredEntity = user[userClaimIds.hipaaCoveredEntity]

    return agreements.reduce<Omit<UseLegalAgreementsResult, 'requiredForScope'>>(
      (acc, agreement) => {
        const acceptedAt = userAcceptedAgreements[agreement.id]
        if (acceptedAt) {
          return {
            ...acc,
            accepted: {
              ...acc.accepted,
              [agreement.id]: acceptedAt,
            },
          }
        }

        const appliesToUser = agreement.appliesToUser({
          category: userCategory,
          hipaaCoveredEntity: userHIPAACoveredEntity,
          acceptedAgreements: userAcceptedAgreements,
        })
        return {
          ...acc,
          ...(appliesToUser.applies && {
            pending: [...acc.pending, agreement.id],
            // filter out any allowed scopes guarded by pending agreement
            allowedScopes: acc.allowedScopes.filter(
              (scope) => agreement.scopesToGuard.indexOf(scope) < 0,
            ),
          }),
        }
      },
      {
        accepted: {},
        pending: [],
        allowedScopes: [GuardedScope.CHECKOUT],
      },
    )
  }, [agreements, isAuthenticated, user])

  // callback that returns which pending agreement ids are required for specified scope
  const requiredForScope: RequiredForScope = useCallback(
    (scope) =>
      agreements
        .filter(
          ({ id, scopesToGuard }) =>
            summary.pending.indexOf(id) >= 0 && scopesToGuard.indexOf(scope) >= 0,
        )
        .map(({ id }) => id),
    [agreements, summary.pending],
  )

  return { ...summary, requiredForScope }
}

export default useLegalAgreements
